import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
  toast,
} from "@devhop/ui";
import {
  Ban,
  CircleDollarSign,
  EditIcon,
  RotateCcwIcon,
  TrashIcon,
} from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { PositionsListResult } from "@/modules/positions/domain/types";
import { RowActions } from "@/shared/ui/RowActions";
import {
  useDeactivatePosition,
  useDeletePosition,
  useReactivatePosition,
} from "../api/queries";

type PositionRow = PositionsListResult["data"][number];

type PositionsTableProps = {
  data: PositionRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (position: PositionRow) => void;
  /** ຈັດການຊ່ວງເງິນເດືອນຕາມອາຍຸໃນຕໍາແໜ່ງ (ຖ້າມີ) */
  onSalaryBands?: (position: PositionRow) => void;
};

export function PositionsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onSalaryBands,
}: PositionsTableProps) {
  const { has } = usePermissions();
  const deactivate = useDeactivatePosition();
  const reactivate = useReactivatePosition();
  const deletePosition = useDeletePosition();

  const columns: TanstackReactTable.ColumnDef<PositionRow>[] = [
    createSortableColumn<PositionRow>("title", "ຊື່ຕໍາແໜ່ງ", { size: 150 }),
    {
      id: "departmentName",
      header: "ພະແນກ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.departmentName || "-"}</span>
      ),
      size: 120,
    },
    createSortableColumn<PositionRow>("level", "ລະດັບ", { size: 80 }),
    {
      id: "parentTitle",
      header: "ລາຍງານໄປຫາ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.parentTitle || "-"}
        </span>
      ),
      size: 120,
    },
    {
      id: "employeeCount",
      header: "ຈໍານວນຄົນ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.employeeCount || 0}</span>
      ),
      size: 80,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) =>
        row.original.status ? (
          <Badge variant="default">ໃຊ້ງານ</Badge>
        ) : (
          <Badge variant="secondary">ບໍ່ໃຊ້ງານ</Badge>
        ),
      size: 100,
    },
    {
      size: 100,
      id: "actions",
      cell: ({ row }: { row: { original: PositionRow } }) => {
        const id = row.original.id as string;
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];

        if (has("positions:update"))
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });

        if (has("positions:update") && onSalaryBands)
          actions.push({
            label: "ເງິນເດືອນຕາມອາຍຸ",
            icon: <CircleDollarSign className="h-4 w-4" />,
            onClick: () => onSalaryBands(row.original),
          });

        if (has("positions:update") && row.original.status)
          actions.push({
            label: "ປິດການໃຊ້ງານ",
            variant: "destructive",
            icon: <Ban className="h-4 w-4" />,
            onClick: async () => {
              const employeeCount = row.original.employeeCount || 0;
              const message =
                employeeCount > 0
                  ? `ຕໍາແໜ່ງນີ້ຍັງມີຄົນຢູ່ ${employeeCount} ຄົນ. ທ່ານແນ່ໃຈບໍ່ວ່າຈະປິດການໃຊ້ງານ?`
                  : "ທ່ານແນ່ໃຈບໍ່ວ່າຈະປິດການໃຊ້ງານຕໍາແໜ່ງນີ້?";
              const ok = await confirm({
                title: "ປິດການໃຊ້ງານຕໍາແໜ່ງ",
                description: message,
                actionText: "ປິດການໃຊ້ງານ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok)
                toast.promise(deactivate.run(id), {
                  loading: "ກໍາລັງປິດການໃຊ້ງານ...",
                  success: "ປິດການໃຊ້ງານຕໍາແໜ່ງສໍາເລັດ",
                  error: "ປິດການໃຊ້ງານຕໍາແໜ່ງລົ້ມເຫຼວ",
                });
            },
          });

        if (has("positions:update") && !row.original.status)
          actions.push({
            label: "ເປີດການໃຊ້ງານ",
            icon: <RotateCcwIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ເປີດການໃຊ້ງານຕໍາແໜ່ງ",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະເປີດການໃຊ້ງານຕໍາແໜ່ງນີ້?",
                actionText: "ເປີດການໃຊ້ງານ",
              });
              if (ok)
                toast.promise(reactivate.run(id), {
                  loading: "ກໍາລັງເປີດການໃຊ້ງານ...",
                  success: "ເປີດການໃຊ້ງານຕໍາແໜ່ງສໍາເລັດ",
                  error: "ເປີດການໃຊ້ງານຕໍາແໜ່ງລົ້ມເຫຼວ",
                });
            },
          });

        if (has("positions:delete"))
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const position = row.original;
              const employeeCount = position.employeeCount || 0;
              const message =
                employeeCount > 0
                  ? `ຕໍາແໜ່ງນີ້ຍັງມີພະນັກງານ ${employeeCount} ຄົນ. ການລຶບຈະລົ້ມເຫຼວຖ້າມີພະນັກງານຫຼືຕໍາແໜ່ງຍ່ອຍ. ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ?`
                  : "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຕໍາແໜ່ງນີ້? ການກະທໍານີ້ບໍ່ສາມາດຍົກເລີກໄດ້.";
              const ok = await confirm({
                title: "ລຶບຕໍາແໜ່ງ",
                description: message,
                actionText: "ລຶບ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok)
                toast.promise(deletePosition.run(id), {
                  loading: "ກໍາລັງລຶບຕໍາແໜ່ງ...",
                  success: "ລຶບຕໍາແໜ່ງສໍາເລັດ",
                  error: (err: Error) => err.message || "ລຶບຕໍາແໜ່ງລົ້ມເຫຼວ",
                });
            },
          });

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<PositionRow, unknown>
      noDataMessage="ບໍ່ພົບຕໍາແໜ່ງ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
